import { Component, OnInit, NgZone, ViewChild } from '@angular/core';
import { AFDisputeQuestionPojo, AsiAttachments, DisputeFileUpload } from '../model/af.model';
import { UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { SkipToMainContentEvent, AttachmentUploadedEvent, BeforeSubmitErrorEvent } from '../service/events';
import { EventService } from '../service/event.service';
import { AuthService } from '../service/auth.service';
import { take } from 'rxjs/operators';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { DisputeFileDialogComponent } from '../dispute-file-dialog/dispute-file-dialog.component';
import { CustomValidatorsService } from '../service/custom-validators.service';

@Component({
  selector: 'app-dispute-questionnaire',
  templateUrl: './dispute-questionnaire.component.html',
  styleUrls: ['./dispute-questionnaire.component.scss']
})

export class DisputeQuestionnaireComponent implements OnInit {

  disputeGroup: UntypedFormGroup;
  disputePojo: AFDisputeQuestionPojo;
  hasBeenSubmitted: boolean = false;
  dataSource = new MatTableDataSource<AsiAttachments>();
  showAttachmentSection: boolean = false;
  displayedColumns: string[] = ['attachmtFileName', 'remove'];
  noAttachment: boolean = false;

  @ViewChild("autosize") autosize: CdkTextareaAutosize;

  @ViewChild(MatPaginator)
  set paginator(value: MatPaginator) {
    this.dataSource.paginator = value;
  }

  constructor(private eventBus: EventService, private dialog: MatDialog, private userService: AuthService, private _ngZone: NgZone, private _snackBar: MatSnackBar,
    private _router: Router, private customValidator: CustomValidatorsService) { }

  ngOnInit() {
    this.subscribeEvents();
  }

  subscribeEvents() {

    this.userService.getDisputeQuestionnaire().subscribe({
      next: (data: AFDisputeQuestionPojo) => {
        this.disputePojo = data;
        if (this.disputePojo.asiAttachments != null) {
          this.dataSource.data = this.disputePojo.asiAttachments;
        }
        this.disputeGroup = this.toFormGroup();
        this.createGroup();
        this.showAttachments();
        this.setValidators();
      },
      error: () => {
        console.log("Error getting dispute questionnaire");
      }
    });

    this.eventBus.on(
      AttachmentUploadedEvent,
      (event): void => {
        if (event.payload.file) {
          let attachment = event.payload.file as AsiAttachments;
          this.dataSource.data.push(attachment);
          this.dataSource._updateChangeSubscription();
        }
      }
    );
  }

  get asiCaseId() { return this.disputeGroup.get('asiCaseId') }
  get asiComponentId() { return this.disputeGroup.get('asiComponentId') }
  get applicantFormId() { return this.disputeGroup.get('applicantFormId') }

  createGroup() {
    //needed for save
    this.disputeGroup.addControl('asiCaseId', new UntypedFormControl(this.disputePojo.asiCaseId || '', Validators.required));
    //also needed for attachment save
    this.disputeGroup.addControl('asiComponentId', new UntypedFormControl(this.disputePojo.asiComponentId || '', Validators.required));
    this.disputeGroup.addControl('applicantFormId', new UntypedFormControl(this.disputePojo.applicantFormId || '', Validators.required));
  }

  //adding dynamic controls
  toFormGroup() {
    let group: any = {};

    this.disputePojo.disputeQuestionAnswer.forEach(question => {
      group[question.questionId] = new UntypedFormControl(this.disputePojo.disputeQuestionAnswerId[question.questionId] || '', [Validators.required]);
    });

    return new UntypedFormGroup(group);
  }

  onFocusOutMainContent(event) {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  showAttachments() {
    if (this.disputeGroup.contains('4')) {
      if ('1' === this.disputeGroup.get('4').value) {
        this.showAttachmentSection = true; //set initial before subscribe
      }
      this.disputeGroup.get('4').valueChanges.subscribe(() => {
        if ('1' === this.disputeGroup.get('4').value) {
          this.showAttachmentSection = true;
        } else {
          this.showAttachmentSection = false;
        }
      });
    }
  }

  setValidators() {
    if (this.disputeGroup.contains('3')) {
      this.disputeGroup.get('3').setValidators(this.customValidator.validateRequiredField);
    }
  }

  removeAttachment(attachmentId) {
    this.userService.removeAttachment(attachmentId).subscribe({
      next: () => {
        this._snackBar.open("Attachment removed successfully", "Close", {
          duration: 4000,
        });

        this.dataSource.data = this.dataSource.data.filter((value, key) => {
          return value.attachmentId != attachmentId;
        });

        this.dataSource._updateChangeSubscription();

      },
      error: (error: any) => console.log(error)
    });
  }

  openFileDialog() {

    this.noAttachment = false;

    let disputeFileUpload: DisputeFileUpload = new DisputeFileUpload();
    disputeFileUpload.applicantFormId = this.applicantFormId.value;
    disputeFileUpload.asiCaseId = this.asiCaseId.value;
    disputeFileUpload.asiComponentId = this.asiComponentId.value;


    const dialogRef = this.dialog.open(DisputeFileDialogComponent, {
      width: '550px',
      role: 'alertdialog',
      data: {
        disputeFileUpload: disputeFileUpload
      }
    });

  }

  triggerResize() {
    // Wait for changes to be applied, then trigger textarea resize.
    this._ngZone.onStable.pipe(take(1))
      .subscribe(() => this.autosize.resizeToFitContent(true));
  }

  onClickSave() {

    this.disputePojo.question = [];
    this.disputePojo.questionOrder = [];
    let i = 0;

    this.disputePojo.disputeQuestionAnswer.forEach(question => {
      for (let controlkey in this.disputeGroup.controls) {
        if (question.questionId.toString() === controlkey) {
          this.disputePojo.disputeQuestionAnswerId[question.questionId] = this.disputeGroup.get(controlkey).value;
          break;
        }
      }
      this.disputePojo.question[question.questionId] = question.questionId.toString();
      this.disputePojo.questionOrder[question.questionId] = i.toString();
      i++;
    });

    this.userService.saveDisputeQuestionnaire(this.disputePojo).subscribe(() => {
      this._snackBar.open("Saved successfully", "Close", {
        duration: 4000,
      });
    });
  }

  onClickSaveAndFinishLater() {
    this.onClickSave();
    this._router.navigate(['sessionTimedOut']);
  }

  onSubmit() {
    if (this.validate()) {

      this.disputePojo.question = [];
      this.disputePojo.questionOrder = [];
      let i = 0;

      this.disputePojo.disputeQuestionAnswer.forEach(question => {
        for (let controlkey in this.disputeGroup.controls) {
          if (question.questionId.toString() === controlkey) {
            this.disputePojo.disputeQuestionAnswerId[question.questionId] = this.disputeGroup.get(controlkey).value;
            break;
          }
        }
        this.disputePojo.question[question.questionId] = question.questionId.toString();
        this.disputePojo.questionOrder[question.questionId] = i.toString();
        i++;
      });

      this.userService.submitDisputeQuestionnaire(this.disputePojo).subscribe(retVal => {
        if ("SUCCESS" == retVal) {
          this._router.navigate(['disputeAlreadySubmitted', { message: 'submit' }]);
        } else {
          this._snackBar.open("Dispute saved unsuccessfully. Please try later.", "Close", {
            duration: 4000,
          });
        }
      });
    } else {
      this.eventBus.emit(new BeforeSubmitErrorEvent({}));
    }
  }

  validate() {
    this.hasBeenSubmitted = true;
    this.disputeGroup.markAllAsTouched();
    if (this.disputeGroup.contains('4')) {
      if ('1' === this.disputeGroup.get('4').value && this.dataSource.data.length < 1) {
        this.noAttachment = true;
        return false;
      } else {
        this.noAttachment = false;
      }
    }
    return !this.disputeGroup.invalid;
  }
}